// Material-style press ripple and hover tint on everything clickable (owner 2026-10-26).
//
// The ripple circle lives inside a clip layer that the script appends to the clicked element, so no theme
// component needs `overflow: hidden` in CSS — the layer clips the circle itself and inherits the element's
// corner radius. The element only gets `position: relative`, and only when it is still static and only on
// first interaction, so nothing changes for elements nobody touches.
//
// Colour comes from `--mm-ripple-color`, falling back to `currentColor`, so a dark button ripples light-on-dark
// and a light one dark-on-light without a per-component rule. Product cards and home tiles set the token to
// ivory in madmire.css, because there the ripple runs over a photo.

// Links inside rich text are left out: a tinted rectangle across a sentence reads as a rendering bug.
const SELECTOR = 'button, summary, [role="button"], a[href], .button, label.variant-option__button-label';
const SKIP = '.rte, .mm-no-ripple';
const VOID_TAGS = new Set(['INPUT', 'SELECT', 'TEXTAREA', 'IMG']);

/**
 * The element a ripple belongs to, or null when the pointer is not over anything clickable.
 * @param {EventTarget | null} target
 * @returns {HTMLElement | null}
 */
function rippleHost(target) {
  if (!(target instanceof Element)) return null;
  const el = target.closest(SELECTOR);
  if (!el || !(el instanceof HTMLElement) || el.closest(SKIP) || VOID_TAGS.has(el.tagName)) return null;
  if (el.matches(':disabled, [aria-disabled="true"]')) return null;
  // Horizon lays a full-card link over the product card, so the ripple would cover the price and the title too.
  // On a card and on a home collection tile it belongs on the photo, the way it already worked before this file.
  const card = el.closest('product-card');
  return card?.querySelector('.card-gallery') ?? el.closest('.mm-tiles__tile') ?? el;
}

/**
 * @param {HTMLElement} el
 * @param {string} modifier
 * @returns {HTMLElement}
 */
function addClipLayer(el, modifier) {
  if (getComputedStyle(el).position === 'static') el.style.position = 'relative';
  const clip = document.createElement('span');
  clip.className = `mm-ripple-clip ${modifier}`;
  el.append(clip);
  return clip;
}

document.addEventListener(
  'pointerdown',
  (e) => {
    const el = rippleHost(e.target);
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    if (x < 0 || y < 0 || x > rect.width || y > rect.height) return;
    const size = 2 * Math.hypot(Math.max(x, rect.width - x), Math.max(y, rect.height - y));
    const clip = addClipLayer(el, 'mm-ripple-clip--press');
    const circle = document.createElement('span');
    circle.className = 'mm-ripple';
    circle.style.cssText = `left:${x}px;top:${y}px;width:${size}px;height:${size}px`;
    clip.append(circle);
    circle.addEventListener('animationend', () => clip.remove());
    // A press that never ends in an animation (reduced motion) still has to clean up after itself.
    setTimeout(() => clip.remove(), 1200);
  },
  { passive: true }
);

// Hover tint. One layer at a time: every pointerover recomputes the host, so leaving everything clickable
// lands on an element whose host is null and takes the layer away. No pointerout bookkeeping.
if (matchMedia('(hover: hover)').matches) {
  /** @type {{ el: HTMLElement, clip: HTMLElement } | null} */
  let current = null;
  document.addEventListener(
    'pointerover',
    (e) => {
      const el = rippleHost(e.target);
      if (el === current?.el && current.clip.isConnected) return;
      current?.clip.remove();
      current = el ? { el, clip: addClipLayer(el, 'mm-ripple-clip--hover') } : null;
    },
    { passive: true }
  );
}
